// material-ui
import { Grid } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React from 'react';

// api
import { ResourceActiveEnabled } from '~/src/dash/api/console-api';
import {
  createFreewheelSite,
  deleteFreewheelSite,
  deleteFreewheelSiteAsso,
  getFreewheelSite,
  IFreewheelSite,
  IFreewheelSiteToSiteAssociation,
  updateFreewheelSite,
} from '~/src/dash/api/freewheel-sites';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  parseLocation,
  RouteFreewheelSiteEdit,
  RouteFreewheelSiteList,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { FreewheelSettingsForm } from '~/src/dash/views/freewheel/freewheel-settings-form';
// views
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  freewheelSite: IFreewheelSite;
  // error messaging
  snackbarMessage: string;
  snackbarError: boolean;
  // ok to render the network settings view
  loaded: boolean;
}

export const FreewheelSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      freewheelSite: {
        ID: 0,
        Name: 'New Freewheel Site',
        PublisherID: parseInt(this.props.session.publisher.ID, 10),
        FreewheelPublisherID: 0,
        Active: ResourceActiveEnabled,
        FreewheelSiteToSiteAssociations: [] as IFreewheelSiteToSiteAssociation[],
      },
      snackbarMessage: '',
      snackbarError: false,
      loaded: false,
    };

    componentDidMount() {
      this.getFreewheelSite();
    }

    getFreewheelSite(): void {
      // based on the url path, this could be a new or edit
      const loc = parseLocation(this.props.location.pathname);
      const freewheelSiteID = loc.freewheelsite;
      const { session, user } = this.props;

      // this is a clone or existing
      if (freewheelSiteID) {
        getFreewheelSite(freewheelSiteID, session.publisher.ID)
          .then((freewheelSite: IFreewheelSite) => {
            this.setState({
              freewheelSite,
              loaded: true,
            });
          })
          .catch(() => {
            this.setState(
              {
                snackbarMessage:
                  "You don't have access to this resource. Please create a new one or select one from the list.",
                snackbarError: true,
              },
              () => {
                setTimeout(() => {
                  navigate(
                    RouteFreewheelSiteList,
                    extractRouteParams({
                      session,
                      user,
                    }),
                  );
                }, 3000);
              },
            );
          });
        // this is a new site
      } else {
        this.setState({
          loaded: true,
        });
      }
    }

    // checks to ensure that one site has no targeting such that the freewheel request
    // will default to this site if no targeting matches
    // also checks to ensure all publica sites are selected
    validateFreewheelSite(freewheelSite: IFreewheelSite): string[] {
      const errorMsgs = [] as string[];
      let defaultSiteExists = false;
      let allPublicaSitesSelected = true;
      for (const site of freewheelSite.FreewheelSiteToSiteAssociations) {
        if (site.SiteID === 0) {
          allPublicaSitesSelected = false;
        }
        if (!defaultSiteExists && site.TargetingDealID === '' && site.TargetingDevices === '0') {
          defaultSiteExists = true;
        }
      }
      if (!defaultSiteExists) {
        errorMsgs.push(
          `One site must have default targeting in case all other sites' targeting fails.`,
        );
      }
      if (!allPublicaSitesSelected) {
        errorMsgs.push(`All sites need to have a Publica Site selected.`);
      }
      if (freewheelSite.Name === '') {
        errorMsgs.push(`Freewheel site must have a name.`);
      }
      if (freewheelSite.FreewheelPublisherID === 0) {
        errorMsgs.push(`Freewheel site must have a Freewheel Publisher ID.`);
      }
      return errorMsgs;
    }

    // -------- HANDLER FUNCTIONS -------- //

    handleClose = () => {
      navigate(
        RouteFreewheelSiteList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // the form will emit a new freewheel site on change and re-render
    handleChange = (freewheelSite: IFreewheelSite) => {
      this.setState({
        freewheelSite,
      });
    };

    // deletes the site and gives user msg if success/failure
    handleDelete = () => {
      deleteFreewheelSite(this.state.freewheelSite.ID.toString(), this.props.session.publisher.ID)
        .then(() => {
          this.setState(
            {
              snackbarMessage: 'Successfully deleted freewheel site',
              snackbarError: false,
            },
            () => {
              setTimeout(() => this.handleClose(), 3000);
            },
          );
        })
        .catch((err) => {
          console.error(err);
          this.setState({
            snackbarMessage: 'Error while deleting freewheel site',
            snackbarError: true,
          });
        });
    };

    // deletes the site asso and gives user msg if success/failure
    handleDeleteFreewheelSiteAsso = (index: number) => {
      const freewheelSite = cloneDeep(this.state.freewheelSite);
      const id = freewheelSite.FreewheelSiteToSiteAssociations[index].ID;
      freewheelSite.FreewheelSiteToSiteAssociations.splice(index, 1);
      deleteFreewheelSiteAsso(
        this.state.freewheelSite.ID.toString(),
        id.toString(),
        this.props.session.publisher.ID,
      )
        .then(() => {
          this.setState(
            {
              snackbarMessage: 'Successfully deleted freewheel site association',
              snackbarError: false,
            },
            () => this.handleChange(freewheelSite),
          );
        })
        .catch((err) => {
          console.error(err);
          this.setState({
            snackbarMessage: 'Error while deleting freewheel site association',
            snackbarError: true,
          });
        });
    };

    // deletes the site and gives user msg if success/failure, returns a promise
    // for ModelDialogDeleteView which requires a promise to close dialog
    handleSubmit = (freewheelSite: IFreewheelSite) => {
      // one default site must exist where all targeting is defaulted
      const errorMsgs = this.validateFreewheelSite(freewheelSite);
      if (errorMsgs.length) {
        this.setState({
          snackbarMessage: errorMsgs.join(','),
          snackbarError: true,
        });
        return;
      }
      if (this.state.freewheelSite.ID === 0) {
        createFreewheelSite(freewheelSite, this.props.session.publisher.ID)
          .then((newFreewheelSite: IFreewheelSite) => {
            this.setState({
              freewheelSite: newFreewheelSite,
            });
            navigate(RouteFreewheelSiteEdit, extractRouteParams(this.props), {
              freewheelsite: newFreewheelSite.ID.toString(),
            });
          })
          .catch((err) => {
            console.error(err);
            this.setState({
              snackbarMessage: `Error while creating freewheel site. ${err}`,
              snackbarError: true,
            });
          });
      } else {
        updateFreewheelSite(freewheelSite, this.props.session.publisher.ID, true)
          .then((newFreewheelSite: IFreewheelSite) => {
            this.setState({
              freewheelSite: newFreewheelSite,
              snackbarMessage: 'Successfully updated freewheel site',
              snackbarError: false,
            });
          })
          .catch((err) => {
            console.error(err);
            this.setState({
              snackbarMessage: `Error while creating freewheel site. ${err}`,
              snackbarError: true,
            });
          });
      }
    };

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render() {
      const { loaded, snackbarMessage, snackbarError, freewheelSite } = this.state;
      if (loaded) {
        return (
          <Grid style={{ marginLeft: 40 }}>
            <FreewheelSettingsForm
              onClose={this.handleClose}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
              onDelete={this.handleDelete}
              onDeleteAsso={this.handleDeleteFreewheelSiteAsso}
              freewheelSite={freewheelSite}
            />
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Grid>
        );
      } else {
        return <LoadingView active={true} text='Loading the freewheel site settings' />;
      }
    }
  },
);
